import clsx from 'clsx';
import { ComponentProps, useEffect, useMemo, useState } from 'react';
import { Modal as RSModal } from 'reactstrap';
import { SectionSectionGroupWithId } from 'src/components/Listings/InventoryEventPage/EventVenueMap/components/v2/SectionGroupPanelV2.hooks';
import {
  toDisplayName,
  toSectionGroupKey,
} from 'src/components/Listings/InventoryEventPage/EventVenueMap/components/v2/SectionGroupPanelV2.utils';
import { VenueZoneSectionGroupDropdownList } from 'src/components/Listings/InventoryEventPage/Sections/VenueZoneMap/components/VenueZoneSectionGroupDropdownList';
import { Content, useContentContext } from 'src/contexts/ContentContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { vars } from 'src/core/themes';
import { Button, Radio, RadioGroup, Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import {
  Feature,
  SectionInfo,
  SectionSectionGroup,
} from 'src/WebApiController';

import * as styles from './ChangeZoneMapSectionGroupDialog.css';

export type ChangeZoneMapDialogProps = ComponentProps<typeof RSModal> & {
  selectedSections: SectionInfo[] | undefined;
  sectionIdMapping: { [key: string]: SectionSectionGroup } | undefined;
  sectionGroups?: SectionSectionGroupWithId[];
  onOkay: (
    sectionSectionGroup: SectionSectionGroup,
    sectionIds: number[]
  ) => void;
  onCancel: () => void;
};

export function ChangeZoneMapSectionGroupDialog({
  selectedSections,
  sectionIdMapping,
  sectionGroups,
  onOkay,
  onCancel,
  ...rest
}: ChangeZoneMapDialogProps) {
  const hasZoneMapV2Feature = useUserHasFeature(Feature.ZoneMapV2);
  const [selectedSectionSectionGroup, setSelectedSectionSectionGroup] =
    useState<SectionSectionGroup | null>(null);

  const selectedSection = useMemo(() => {
    return selectedSections?.[0];
  }, [selectedSections]);

  useEffect(() => {
    const sectionId = selectedSection?.id;
    const sectionSectionGroup =
      sectionIdMapping && sectionId ? sectionIdMapping[sectionId] : undefined;

    if (sectionSectionGroup) {
      setSelectedSectionSectionGroup(sectionSectionGroup);
    }
  }, [sectionIdMapping, selectedSection?.id]);

  const selectedSectionGroupKey = useMemo(() => {
    if (!selectedSectionSectionGroup) return undefined;
    return toSectionGroupKey(selectedSectionSectionGroup);
  }, [selectedSectionSectionGroup]);

  if (!selectedSections?.length || !selectedSection) {
    return null;
  }

  return (
    <>
      <GenericDialog
        {...rest}
        size="m"
        header={<Content id={ContentId.ChangeZone} />}
        footer={
          <>
            <Button className={styles.cancelButton} onClick={onCancel}>
              <Content id={ContentId.Cancel} />
            </Button>
            <Button
              disabled={selectedSectionSectionGroup == null}
              onClick={() => {
                if (selectedSectionSectionGroup != null) {
                  onOkay(
                    selectedSectionSectionGroup,
                    selectedSections!.map((s) => s.id)
                  );
                }
              }}
            >
              <Content id={ContentId.Save} />
            </Button>
          </>
        }
        onCancel={onCancel}
      >
        <div style={{ marginBottom: vars.spacing.m }}>
          <Content id={ContentId.ChangeGroup} />
        </div>

        {hasZoneMapV2Feature ? (
          <Stack
            direction="column"
            width="full"
            gap="s"
            style={{ flex: 1, maxHeight: '40vh', overflowY: 'auto' }}
          >
            <RadioGroup value={selectedSectionGroupKey}>
              <Stack direction="column" gap="s">
                {(sectionGroups ?? []).map(({ id, ...sectionGroup }) => (
                  <Radio
                    key={id}
                    value={id}
                    label={<SectionGroupItem sectionGroup={sectionGroup} />}
                    containerClassName={clsx(styles.sectionGroupRadio, {
                      [styles.sectionGroupRadioActive]:
                        selectedSectionSectionGroup &&
                        id === selectedSectionGroupKey,
                    })}
                    onClick={() =>
                      setSelectedSectionSectionGroup(
                        sectionGroup as SectionSectionGroup
                      )
                    }
                  ></Radio>
                ))}
              </Stack>
            </RadioGroup>
          </Stack>
        ) : (
          <VenueZoneSectionGroupDropdownList
            selectedSectionSectionGroup={selectedSectionSectionGroup}
            setSelectedSectionSectionGroup={setSelectedSectionSectionGroup}
          />
        )}
      </GenericDialog>
    </>
  );
}

const SectionGroupItem = ({
  sectionGroup,
}: {
  sectionGroup: SectionSectionGroup;
}) => {
  const contentContext = useContentContext();
  return (
    <Stack alignItems="center" gap="s">
      <div
        className={styles.groupColorBox}
        style={{ backgroundColor: sectionGroup.sectionGroupColor }}
      />
      <span>{toDisplayName(sectionGroup, contentContext)}</span>
    </Stack>
  );
};
